<template>
  <div
    id="ticket-update-list-container"
    class="w-full bg-white border border-gray-200 rounded shadow-sm"
  >
    <div
      id="ticket-update-list-card"
      class="w-full p-2.5"
    >
      <div
        id="ticket-update-list-header"
        class="w-full flex flex-row justify-between items-center p-4"
      >
        <div class="flex flex-col justify-start items-start">
          <p
            id="ticket-update-list-title"
            class="text-orange-organization flex-1"
          >
            Atualizações e histórico de e-mails
          </p>
          <p
            class="mt-1 text-gray-800"
            :title="showEmailClient"
          >
            Para: {{showEmailClient}}
          </p>
        </div>
        <!--Componente normal-->
        <div
          class="hidden sm:relative sm:flex sm:flex-col sm:justify-start sm:items-end"
        >
          <button
            id="get-attachments"
            class="flex flex-row justify-start items-center py-1 px-3 uppercase text-gray-800 cursor-pointer font-medium hover:bg-gray-200 active:bg-gray-400 disabled:text-gray-500 disabled:bg-transparent disabled:cursor-default rounded"
            @click.stop.prevent="toggleAttachmentsDropdownMenu"
            :disabled="loading || getAttachmentsDisabled"
            :title="getAttachmentsDisabled ? 'Este atendimento não possui anexos' : ''"
          >
            <span>
              {{ getAttachmentsLabel }}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 ml-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
          </button>

          <button
            id="get-email-report"
            class="flex flex-row justify-start items-center py-1 px-3 mt-1 uppercase text-gray-800 cursor-pointer font-medium hover:bg-gray-200 active:bg-gray-400 disabled:text-gray-500 disabled:bg-transparent disabled:cursor-default rounded"
            :disabled="loading || getEmailsDisabled"
            @click="generateEmailCommentsReport()"
          >
            <span>
              Baixar histórico de e-mails
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 ml-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </button>
          <div
            v-show="ticketAttachmentDropdown"
            id="attachments-dropdown-menu"
            ref="attachmentsDropdownMenu"
            class="absolute right-2 top-8 py-2 w-56 bg-white rounded shadow-lg border z-20 lg:right-14 lg:top-2"
          >
            <p
              v-for="(attachment, index) in attachments"
              :id="`ticket-attachment-${index}`"
              :key="`ticket-attachment-${index}`"
              class="px-2 py-2 text-sm text-blue-500 underline cursor-pointer hover:text-blue-800 truncate hover:bg-gray-100"
              :title="attachment.filename | formatFileName"
              @click="openAttachment(attachment._id.toString())"
            >
              {{ attachment.filename | formatFileName }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="!loading"
        class="w-full relative flex flex-col justify-start items-stretch px-4 mt-2">
        <ExternalTicketUpdateItem
            v-for="(history, key) in histories"
            :key="key"
            :history="history"
            :last="key === histories.length - 1"
            />
        <ExternalTicketUpdateItemFooter :last-status="lastStatus"/>
      </div>

    </div>

    <ExternalSnackbar
      :show="snackBar.show"
      @closed="snackBar.show = false"
    >
      {{ snackBar.message }}
    </ExternalSnackbar>

    <ExternalLoading
      v-if="loading"
      text="Carregando informações do atendimento"
    />

  </div>
</template>

<script>
import ExternalTicketUpdateItem from '@/components/ExternalTicketUpdateItem'
import ExternalTicketUpdateItemFooter from '@/components/ExternalTicketUpdateItemFooter'
import ExternalSnackbar from '@/components/commons/ExternalSnackbar'
import ExternalLoading from '@/components/commons/ExternalLoading'
import {
  findOne as TicketFindOne,
  history as ticketHistory,
  generateReportEmails as ticketGenerateReportEmails
} from '@/repositories/integrations/mobly/services/csExternal/ticket'
import { findOne as UploadFindOne } from '@/repositories/integrations/mobly/services/csExternal/upload'

export default {
  name: 'ExternalTicketUpdateList',
  components: {
    ExternalTicketUpdateItem,
    ExternalTicketUpdateItemFooter,
    ExternalSnackbar,
    ExternalLoading
  },
  props: {
    ticketId: String,
    organization: String
  },
  data () {
    return {
      histories: [],
      lastStatus: {},
      getEmailsDisabled: true,
      getAttachmentsDisabled: true,
      attachments: [],
      snackBar: {
        show: false,
        message: ''
      },
      loading: false,
      ticketAttachmentDropdown: false,
      showEmailClient: ''
    }
  },
  computed: {
    getAttachmentsLabel () {
      return this.attachments.length > 0
        ? `Baixar anexos (${this.attachments.length})`
        : 'Baixar anexos (0)'
    }
  },
  filters: {
    formatFileName: value => (value.split('] ')[1])
  },
  mounted () {
    this.init()
  },
  updated () {
    if (
      this.$route && this.$route.query && this.$route.query.anchor &&
      this.$route.query.anchor === 'true'
    ) {
      this.scrollToBottom()
    }
  },
  destroyed () {
    window.removeEventListener('click', this.closeTicketAttachmentsMenu)
  },
  methods: {
    async init () {
      await this.getTicketHistoryAndUpdates()
      await this.getTicketAttachments()
    },
    async getTicketHistoryAndUpdates () {
      try {
        this.loading = true

        const ticket = await ticketHistory(this.ticketId, this.organization)

        if (ticket.data.histories[0]) this.getEmailsDisabled = false

        this.histories = ticket.data.histories
        this.lastStatus = ticket.data.ticket.lastStatus

        this.loading = false
      } catch (error) {
        this.loading = false

        this.snackBar.message = error.response.data.message
        this.snackBar.show = true
      }
    },
    async getTicketAttachments () {
      const response = await TicketFindOne(this.ticketId, '', this.organization)

      this.showEmailClient = response.data.emails[0].to

      const attachments = this.getUploadsFromSharedFiles(
        this.getSharedFiles(response.data.shared)
      )

      this.attachments = attachments

      if (attachments[0]) this.getAttachmentsDisabled = false

      this.$emit('attachments-loaded', attachments)
    },
    getSharedFiles (data) {
      const sharedFiles = []

      data.forEach(
        shared => {
          shared.fields.forEach(field => {
            if (field.type === 'file' && field.show && field.showFromBack !== false) {
              sharedFiles.push(field)
            }
          })
        }
      )
      return sharedFiles
    },
    getUploadsFromSharedFiles (files) {
      const attachments = []
      files.forEach(file => {
        if (file.value && file.value.uploads && Array.isArray(file.value.uploads)) {
          file.value.uploads.forEach(upload => {
            attachments.push(upload)
          })
        }
      })
      return attachments
    },
    async generateEmailCommentsReport () {
      try {
        this.snackBar.message = 'Gerando histórico de e-mails'
        this.snackBar.show = true

        const ticketCod = await TicketFindOne(this.ticketId, '', this.organization)

        const commentsResponse = await ticketGenerateReportEmails(this.ticketId,
          { ticketCod: ticketCod.data.cod }, this.organization
        )

        const buf = Buffer.from(commentsResponse.data, 'base64')
        const pdfUrl = window.URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
        const tempLink = document.createElement('a')
        tempLink.href = pdfUrl
        tempLink.download = `historico_de_emails_${new Date().toLocaleString()}.pdf`
        tempLink.click()
        tempLink.remove()
        setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 100)
      } catch (error) {
        this.snackBar.message = error.message || 'Ocorreu um erro inesperado ao gerar o relatório'
        this.snackBar.show = true
      }
    },
    async openAttachment (attachmentId) {
      if (this.loading) return
      try {
        this.loading = true
        const queryParams = 'contentDisposition=inline'
        const upload = await UploadFindOne(attachmentId, queryParams, this.organization)
        window.open(upload.data.url)
      } catch (error) {
        this.snackBar.message = error.message || 'Erro inesperado ao buscar anexo'
        this.snackBar.show = true
      } finally {
        this.loading = false
      }
    },
    closeTicketAttachmentsMenu (e) {
      const ticketsAttachmentMenu = this.$refs.attachmentsDropdownMenu

      if (ticketsAttachmentMenu === e.target) return

      if (this.ticketAttachmentDropdown) this.ticketAttachmentDropdown = false

      window.removeEventListener('click', this.closeTicketAttachmentsMenu)
    },
    toggleAttachmentsDropdownMenu () {
      if (this.ticketAttachmentDropdown) {
        this.ticketAttachmentDropdown = false
        window.removeEventListener('click', this.closeTicketAttachmentsMenu)
        return
      }

      this.ticketAttachmentDropdown = true
      window.addEventListener('click', this.closeTicketAttachmentsMenu)
    },
    scrollToBottom () {
      if (this.histories && this.histories.length > 1) {
        this.$el.scrollIntoView(false)
      }
    }
  }
}
</script>
